import { useState, useMemo, useCallback } from 'react';
import PageList from '../../templates/PageList';
import BadgeStatus from '../../atoms/BadgeStatus';
import appConfig from '../../../config/app-config';
import ReversalModal from './modals/ReversalModal';
import PaymentModal, { PaymentMode } from './modals/PaymentModal';
import MetadataModal from './modals/MetadataModal';
import MovementsModal from './modals/MovementsModal';
import { FilterBuilder } from '../../molecules/FilterBuilder';
import { AppliedFilter } from '../../molecules/FilterBuilder/types';
import { settlementFilterSchema, serializeFilters } from './constants/filterSchemas';
import { FaInfoCircle } from 'react-icons/fa';

type BadgeColor = 'blue' | 'green' | 'red' | 'orange' | 'gray' | 'yellow' | 'purple' | 'indigo' | 'pink';

const statusColors: Record<string, BadgeColor> = {
    OPEN: 'yellow',
    PAID: 'green',
    PARTIALLY_PAID: 'orange',
    SUPERSEDED: 'gray',
    CANCELLED: 'red',
    PAID_WITH_LOSS: 'orange',
};

const formatCurrency = (value: string) => {
    const num = parseFloat(value || '0');
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatDate = (date: string | null) => {
    if (!date) return '-';
    const [y, m, d] = date.split('-');
    return `${d}/${m}/${y}`;
};

const TitleDebtPageList = () => {
    const [currentPage, setCurrentPage] = useState(0);
    const [currentPageSize, setCurrentPageSize] = useState(100);
    const [currentSortField, setCurrentSortField] = useState('due_date');
    const [currentSortOrder, setCurrentSortOrder] = useState<'ASC' | 'DESC'>('ASC');
    const [selectedDebt, setSelectedDebt] = useState<any>(null);
    const [activeFilters, setActiveFilters] = useState<AppliedFilter[]>([]);
    const [refreshKey, setRefreshKey] = useState(0);
    const [metadataDebt, setMetadataDebt] = useState<any>(null);

    const { address: API_URL, key: API_KEY } = appConfig.apis.settlementV2;

    const sortParam = currentSortOrder === 'DESC' ? `-${currentSortField}` : currentSortField;
    const params = useMemo(() => {
        const p = new URLSearchParams({
            page: String(currentPage + 1),
            size: String(currentPageSize),
            sort: sortParam,
        });
        // Merge active filters into query params
        const filterParams = serializeFilters(activeFilters);
        filterParams.forEach((value, key) => p.set(key, value));
        return p;
    }, [currentPage, currentPageSize, sortParam, activeFilters]);

    const url = `${API_URL}/title-debts?${params.toString()}&_r=${refreshKey}`;

    const handleFilterSearch = useCallback((filters: AppliedFilter[]) => {
        setActiveFilters(filters);
        setCurrentPage(0);
    }, []);

    const fetchHeaders = useMemo(() => [
        { key: 'x-api-key', value: API_KEY || '' }
    ], [API_KEY]);

    const tableHeaders = useMemo(() => [
        { id: 'contract_number', title: 'Contrato', canSort: true, field: 'contract_number' },
        { id: 'document_title', title: 'Título/NF', canSort: false },
        { id: 'contract_operation_id', title: 'Operação', canSort: false },
        { id: 'installment_number', title: 'Parcela', canSort: true, field: 'installment_number' },
        { id: 'amendment_number', title: 'Aditamento', canSort: true, field: 'amendment_number' },
        { id: 'amount', title: 'Valor', canSort: true, field: 'amount' },
        { id: 'paid_amount', title: 'Pago', canSort: true, field: 'paid_amount' },
        { id: 'due_date', title: 'Vencimento', canSort: true, field: 'due_date' },
        { id: 'days_overdue', title: 'Dias Atraso', canSort: true, field: 'days_overdue' },
        { id: 'status', title: 'Status', canSort: true, field: 'status' },
        { id: 'actions', title: 'Ações', canSort: false },
    ], []);

    const handleRowClick = (row: any) => setSelectedDebt(row);

    const pageSorted = (field: string, order: 'ASC' | 'DESC') => {
        setCurrentSortField(field);
        setCurrentSortOrder(order);
    };
    const pageChanged = (newPage: number) => setCurrentPage(newPage);
    const pageSizeChanged = (newSize: number) => { setCurrentPageSize(newSize); setCurrentPage(0); };

    // o modal de pagamento já abre com o tipo de liquidação definido pelo botão
    const [paymentDebt, setPaymentDebt] = useState<{ row: any; mode: PaymentMode } | null>(null);
    const [reversalDebt, setReversalDebt] = useState<any>(null);

    const renderRows = (rows: any[]) => {
        return rows.map((row: any) => (
            <tr key={row.id} className={`hover:bg-gray-50 cursor-pointer ${row.movements_count > 0 ? 'hover:bg-blue-50' : ''}`} onClick={() => handleRowClick(row)}>
                <td className="px-4 py-2 text-sm font-medium">{row.contract_number || '-'}</td>
                <td className="px-4 py-2 text-sm">{row.document_title || '-'}</td>
                <td className="px-4 py-2 text-sm text-gray-500" title={row.contract_operation_id}>{row.contract_operation_id?.slice(0, 8)}</td>
                <td className="px-4 py-2 text-sm text-center">{row.installment_number || '-'}</td>
                <td className="px-4 py-2 text-sm text-center">{row.amendment_number > 0 ? row.amendment_number : '-'}</td>
                <td className="px-4 py-2 text-sm text-right">{formatCurrency(row.amount)}</td>
                <td className="px-4 py-2 text-sm text-right">{formatCurrency(row.paid_amount)}</td>
                <td className="px-4 py-2 text-sm">{formatDate(row.due_date)}</td>
                <td className="px-4 py-2 text-sm text-center">
                    {row.days_overdue > 0 ? <span className="text-red-600 font-medium">{row.days_overdue}d</span> : '-'}
                </td>
                <td className="px-4 py-2 text-sm">
                    <BadgeStatus text={row.status} color={statusColors[row.status] || 'gray'} />
                    {/* "AVAL a devolver" saiu do title_debt (changelog_024) e agora vive na
                        fatura — o selo pertence à tela de faturas do billing */}
                </td>
                <td className="px-4 py-2 text-sm" onClick={e => e.stopPropagation()}>
                    {row.data && (
                        <button
                            className="px-2 py-1 text-xs bg-slate-100 text-slate-600 rounded hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600 mr-1"
                            onClick={() => setMetadataDebt(row)}
                            title="Ver metadata"
                        ><FaInfoCircle /></button>
                    )}
                    {row.status === 'OPEN' && (
                        <>
                            <button
                                className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                                onClick={() => setPaymentDebt({ row, mode: 'TOTAL' })}
                            >Liquidar</button>
                            <button
                                className="px-2 py-1 text-xs bg-amber-500 text-white rounded hover:bg-amber-600 ml-1"
                                onClick={() => setPaymentDebt({ row, mode: 'PARCIAL' })}
                            >Baixa Parcial</button>
                        </>
                    )}
                    {row.can_reverse && (
                        <button
                            className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 ml-1"
                            onClick={() => setReversalDebt(row)}
                        >Reverter</button>
                    )}
                </td>
            </tr>
        ));
    };

    return (
        <>
            <PageList
                pageTitle="Títulos de Dívida"
                url={url}
                fetchHeaders={fetchHeaders}
                breadcrumb={[
                    { label: 'Home', url: '/home' },
                    { label: 'Financeiro', url: '#' },
                    { label: 'Títulos de Dívida', url: '/settlement/title-debts' },
                ]}
                tableHeaders={tableHeaders}
                defaultBackendHeader={false}
                tableFieldSort={currentSortField}
                tableOrderSort={currentSortOrder}
                onTableSort={pageSorted}
                renderRows={renderRows}
                page={currentPage}
                pageSize={currentPageSize}
                onPageChange={pageChanged}
                onItemsPerPageChange={pageSizeChanged}
                itemsLabel="títulos"
            >
                {{
                    filterSlot: (
                        <FilterBuilder
                            schema={settlementFilterSchema}
                            onSearch={handleFilterSearch}
                        />
                    ),
                    controlSlot: (<div></div>),
                }}
            </PageList>
            {selectedDebt && (
                <MovementsModal
                    titleDebt={selectedDebt}
                    onClose={() => setSelectedDebt(null)}
                />
            )}
            {paymentDebt && (
                <PaymentModal
                    titleDebt={paymentDebt.row}
                    mode={paymentDebt.mode}
                    onClose={() => setPaymentDebt(null)}
                    onSuccess={() => setRefreshKey(k => k + 1)}
                />
            )}
            {reversalDebt && (
                <ReversalModal
                    titleDebt={reversalDebt}
                    onClose={() => setReversalDebt(null)}
                    onSuccess={() => setRefreshKey(k => k + 1)}
                />
            )}
            {metadataDebt && (
                <MetadataModal
                    data={metadataDebt.data}
                    title={`Metadata — ${metadataDebt.contract_number || ''} ${metadataDebt.document_title ? `(${metadataDebt.document_title})` : `P${metadataDebt.installment_number || ''}`}`}
                    onClose={() => setMetadataDebt(null)}
                />
            )}
        </>
    );
};

export default TitleDebtPageList;
